import type { ReactElement } from "react";
import type { Rules } from "odyssey-shared";
import { say } from "../../words.ts";
import { pendingTotal, previewOf } from "./allocation.ts";
import { statDelta, statHelps, statName, statValue } from "./stat-words.ts";
import type { CharacterSnapshot } from "./store.ts";

/**
 * The preview panel (ADR-138's amendment of 2026-10-31, decision 5; the author's ruling 5): beside the Character tab
 * while points are pending, each derived stat the choice changes, now and after, with an arrow and the delta, from the
 * same shared formulas the server's factors come from. It changes on every + and -, before Confirm.
 */

/**
 * The panel, or nothing while no point is pending.
 *
 * @param props The snapshot, the rules and the character's level.
 * @returns The panel.
 */
export function PreviewPanel({ snapshot, rules, level }: { readonly snapshot: CharacterSnapshot; readonly rules: Rules; readonly level: number }): ReactElement | null {
  const frame = snapshot.frame;
  if (frame === null || pendingTotal(snapshot.pending) === 0) {
    return null;
  }
  const rows = previewOf(frame, snapshot.pending, level, rules);
  return (
    <section className="ot-character-preview" data-character="preview" aria-label={say("character.preview.title")}>
      <h3 className="ot-character-preview__title">{say("character.preview.title")}</h3>
      {rows.length === 0 ? (
        <p className="ot-character-preview__empty">{say("character.preview.empty")}</p>
      ) : (
        <ul className="ot-character-preview__rows">
          {rows.map((row) => (
            <li key={row.id} className="ot-character-preview__row" data-stat={row.id}>
              <span className="ot-character-preview__name">{statName(row.id)}</span>
              <span className="ot-character-preview__values">
                <span className="ot-character-preview__before">{statValue(row.id, row.before)}</span>
                <span className="ot-character-preview__arrow" aria-hidden="true" />
                <span className="ot-character-preview__after">{statValue(row.id, row.after)}</span>
                <span className={statHelps(row) ? "ot-character-preview__delta is-good" : "ot-character-preview__delta is-bad"}>{statDelta(row)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
